import { useMemo, useState } from 'react';
import { ArrowRight, Filter, Phone, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import Hero from '../elements/Hero';
import { shopConditions, shopInventory, shopTypes } from '../data/shopInventory';

const sortOptions = [
  ['featured', 'Featured'],
  ['condition', 'Condition'],
  ['type', 'Baler type'],
  ['title', 'A-Z'],
];

function Shop() {
  const [condition, setCondition] = useState('all');
  const [type, setType] = useState('all');
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState('featured');

  const listings = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return shopInventory
      .filter((item) => condition === 'all' || item.condition === condition)
      .filter((item) => type === 'all' || item.type === type)
      .filter((item) => {
        if (!normalizedQuery) return true;
        return [item.title, item.condition, item.type, item.lead, ...item.specs]
          .join(' ')
          .toLowerCase()
          .includes(normalizedQuery);
      })
      .sort((a, b) => {
        if (sortBy === 'condition') return a.condition.localeCompare(b.condition);
        if (sortBy === 'type') return a.type.localeCompare(b.type);
        if (sortBy === 'title') return a.title.localeCompare(b.title);
        return 0;
      });
  }, [condition, query, sortBy, type]);

  return (
    <>
      <Hero
        eyebrow="Current inventory"
        title="Industrial Balers For Sale"
        description="Browse available C&M balers, rebuilt machines, handling packages, and shop-ready equipment for recycling, paper, textile, warehouse, and manufacturing operations."
        image="equipment/horizontal-balers/horizontal-5.jpg"
        compact
      />

      <main className="shop-main">
        <section className="shop-intro">
          <div>
            <span className="eyebrow">Shop C+M equipment</span>
            <h2>Available Balers, Configured Around Real Material Streams</h2>
            <p className="body-copy">
              Browse current balers, rebuilt equipment, and material handling systems available through C&M Baling Systems.
            </p>
          </div>
        </section>

        <section className="shop-layout" aria-label="Shop inventory">
          <aside className="shop-filters" aria-label="Shop filters">
            <div className="shop-filter-heading">
              <Filter size={18} />
              <span>Filter inventory</span>
            </div>

            <label>
              <span>Search</span>
              <div className="shop-search">
                <Search size={17} />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Model, material, type"
                />
              </div>
            </label>

            <label>
              <span>Condition</span>
              <select value={condition} onChange={(event) => setCondition(event.target.value)}>
                <option value="all">All conditions</option>
                {shopConditions.map((item) => (
                  <option key={item} value={item}>{item}</option>
                ))}
              </select>
            </label>

            <label>
              <span>Baler type</span>
              <select value={type} onChange={(event) => setType(event.target.value)}>
                <option value="all">All baler types</option>
                {shopTypes.map((item) => (
                  <option key={item} value={item}>{item}</option>
                ))}
              </select>
            </label>

            <Link className="shop-filter-quote" to="/contact/request-quote">
              Request matching help <ArrowRight size={15} />
            </Link>
          </aside>

          <div className="shop-results">
            <div className="shop-toolbar">
              <strong>{listings.length} listings</strong>
              <label>
                <span>Sort</span>
                <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
                  {sortOptions.map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="shop-grid">
              {listings.map((item) => (
                <article className="shop-card" key={item.id}>
                  <Link className="shop-card-image" to={item.href} aria-label={`View ${item.title}`}>
                    <img src={item.image} alt={item.title} />
                    <span>{item.condition}</span>
                  </Link>
                  <div className="shop-card-body">
                    <small>{item.type}</small>
                    <h2>{item.title}</h2>
                    <p>{item.lead}</p>
                    <div className="shop-specs">
                      {item.specs.map((spec) => (
                        <span key={spec}>{spec}</span>
                      ))}
                    </div>
                    <div className="shop-card-foot">
                      <strong>{item.price}</strong>
                      <Link to={item.href}>Details <ArrowRight size={15} /></Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {listings.length === 0 ? (
              <div className="shop-empty">
                <h2>No listings match those filters</h2>
                <p>Clear a filter or contact C&M for current equipment availability.</p>
              </div>
            ) : null}
          </div>
        </section>

        <section className="band shop-cta">
          <div>
            <span className="eyebrow">Need current availability?</span>
            <h2>Talk through the material, volume, loading method, and bale requirements.</h2>
            <p>C&M can help match an available baler, refurbishing option, or new build to the job.</p>
          </div>
          <a className="button light" href="tel:8002253796"><Phone size={16} /> (800) 225-3796</a>
        </section>
      </main>
    </>
  );
}

export default Shop;
